import type { ChestSource } from './chests';

/**
 * What a chest was FOR, in words: one function for every place a chest appears (the arrival's
 * title, the chest screen's kicker, the notification and its toast, the Rewards history), shared
 * by the client and the server so the words never drift apart.
 *
 *   caps       "2ND PLACE · WEEKLY $CHAIN", "5 WINS IN A ROW"  the arrival, the kicker, a Rewards row's title
 *   sentence   "2nd place on the weekly $CHAIN board", "5 wins in a row"  a notification, a toast, the inbox
 *              (`chestNoticeLine` puts the rarity in front: "Epic chest for 2nd place on the weekly $CHAIN board")
 *
 * The weekly-top label reads its place and board from the award's ref
 * (`<board>:<ISO week>:<place>`); a ref it cannot read (an older or malformed one) falls back to
 * the source's own label ("WEEKLY TOP 3").
 */

export type ChestLabelStyle = 'caps' | 'sentence';

/** The words per source, caps and sentence; both within `LONG` characters or they wrap. */
export const CHEST_SOURCE_LABEL: Record<ChestSource, { caps: string; sentence: string }> = {
  rank_up: { caps: 'RANK-UP CHEST', sentence: 'Rank-up chest' },
  first_win: { caps: 'FIRST WIN', sentence: 'First win' },
  wins_10: { caps: '10 WINS', sentence: '10 wins' },
  wins_50: { caps: '50 WINS', sentence: '50 wins' },
  wins_100: { caps: '100 WINS', sentence: '100 wins' },
  win_streak_3: { caps: 'WIN STREAK', sentence: 'Win streak' },
  login_streak_7: { caps: '7-DAY STREAK', sentence: '7-day streak' },
  weekly_top: { caps: 'WEEKLY TOP 3', sentence: 'Weekly top 3' },
  welcome: { caps: 'WELCOME GIFT', sentence: 'Welcome gift' },
};

/**
 * The leaderboard's tab names (src/ui/leaderboard-data.ts `BOARDS`, `label`), by the board key in a weekly ref
 * (server/src/leaderboard.ts `PAID_BOARDS`). 21 Wild's own: its `combo` board is "Card streaks" (Blockari's is "Combos").
 */
const BOARD_NAME: Record<string, string> = {
  xp: 'XP',
  won: '$CHAIN',
  score: 'Best score',
  combo: 'Card streaks',
  streak: 'Win streaks',
};

/** The board name as it reads inside a sentence: the pill's, lower-cased except XP and $CHAIN. */
const inSentence = (board: string): string =>
  board === 'XP' || board === '$CHAIN' ? board : board.toLowerCase();

const ORDINAL: Record<string, string> = { '1': '1st', '2': '2nd', '3': '3rd' };

export interface ChestLabelInput {
  source: ChestSource;
  sourceRef?: string | undefined;
}

/** The run a streak chest was earned at, from its ref (`streak-<run>-<challengeId>`); null for older refs. */
export function streakRun(ref: string | undefined): number | null {
  const m = /^streak-(\d{1,4})-/.exec(ref ?? '');
  return m?.[1] ? Number(m[1]) : null;
}

/** A weekly ref's board name and place ("$CHAIN", "2nd"), or null when it is not one the label can read. */
function weeklyParts(ref: string | undefined): { board: string; place: string } | null {
  const [board, week, place, ...rest] = (ref ?? '').split(':');
  if (rest.length > 0 || !board || !week || !place) return null;
  const name = Object.hasOwn(BOARD_NAME, board) ? BOARD_NAME[board] : undefined;
  const ord = Object.hasOwn(ORDINAL, place) ? ORDINAL[place] : undefined;
  return name && ord ? { board: name, place: ord } : null;
}

/** What the chest was for. See the file header for the two styles. */
export function chestLabel(c: ChestLabelInput, style: ChestLabelStyle = 'caps'): string {
  if (c.source === 'win_streak_3') {
    const run = streakRun(c.sourceRef);
    if (run) return style === 'caps' ? `${run} WINS IN A ROW` : `${run} wins in a row`;
  }
  if (c.source === 'weekly_top') {
    const w = weeklyParts(c.sourceRef);
    if (w) {
      if (style === 'sentence')
        return `${w.place} place on the weekly ${inSentence(w.board)} board`;
      return `${w.place} place \u00b7 weekly ${w.board}`.toUpperCase();
    }
  }
  return CHEST_SOURCE_LABEL[c.source][style];
}

/**
 * A chest notice's one line (the notification, its push body and its toast): "Rare chest · 5 wins
 * in a row", and for a weekly place, which reads as a sentence, "Epic chest for 2nd place on the
 * weekly $CHAIN board". Takes the rarity and the `sentence` label as the notice row keeps them.
 */
export function chestNoticeLine(rarity: string, label: string): string {
  const chest = `${rarity.charAt(0).toUpperCase()}${rarity.slice(1)} chest`;
  return /^[1-3](?:st|nd|rd) place /.test(label)
    ? `${chest} for ${label}`
    : `${chest} \u00b7 ${label}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The days an ISO week ("2026-W43") spans, as "Oct 19–25" (or "Oct 26–Nov 1" across a month),
 * in UTC like the weekly reset; null for anything that is not an ISO week.
 */
export function isoWeekRange(week: string): string | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!m) return null;
  const year = Number(m[1]);
  const n = Number(m[2]);
  if (n < 1 || n > 53) return null;
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const mondayOfWeek1 = jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86_400_000;
  const start = new Date(mondayOfWeek1 + (n - 1) * 7 * 86_400_000);
  const end = new Date(start.getTime() + 6 * 86_400_000);
  const a = `${MONTHS[start.getUTCMonth()]} ${start.getUTCDate()}`;
  const b =
    end.getUTCMonth() === start.getUTCMonth()
      ? String(end.getUTCDate())
      : `${MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}`;
  return `${a}–${b}`;
}

/** The week a weekly-top chest was won for ("Oct 19–25"), from its ref; null for any other chest or an unreadable ref. */
export function chestWeek(c: ChestLabelInput): string | null {
  if (c.source !== 'weekly_top' || !weeklyParts(c.sourceRef)) return null;
  return isoWeekRange((c.sourceRef ?? '').split(':')[1] ?? '');
}

/**
 * The dim line under a Rewards history row's title: a weekly place says it was a top-3 reward, and
 * which week ("Top 3 reward · Oct 19–25"); null for any other chest or an unreadable ref (its
 * title, "WEEKLY TOP 3", already says it).
 */
export function chestSubline(c: ChestLabelInput): string | null {
  if (c.source !== 'weekly_top' || !weeklyParts(c.sourceRef)) return null;
  const week = chestWeek(c);
  return week ? `Top 3 reward \u00b7 ${week}` : 'Top 3 reward';
}
